<script setup lang="ts">
import type { DocumentNode } from 'graphql';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axios from 'axios';
import { print } from 'graphql/language/printer';
import { onUnmounted } from 'vue';
import { useConfig } from '~/lib/fw/useFw';

interface Props {
  query: DocumentNode;
  queryKey: any[];
  variables?: any;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  removeCacheOnUnmount?: boolean;
  enabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  removeCacheOnUnmount: false,
  enabled: true,
});

const config = useConfig();

const { isPending, isSuccess, isError, data, error, refetch, isFetching } =
  useQuery({
    queryKey: props.queryKey,
    queryFn: async () =>
      (
        await axios.post(
          `${config.apiUrl}/graphql`,
          {
            query: print(props.query),
            variables: props.variables ?? {},
          },
          {
            getAccessToken: true,
          } as any,
        )
      ).data?.data,
    retry: props.retry,
  });

const queryClient = useQueryClient();

async function reload() {
  queryClient.removeQueries({
    queryKey: props.queryKey,
  });

  await refetch();
}

onUnmounted(() => {
  if (props.removeCacheOnUnmount) {
    queryClient.removeQueries({
      queryKey: props.queryKey,
    });
  }
});

defineExpose({ refetch, reload });
</script>

<template>
  <slot v-if="isPending" name="loading"></slot>
  <slot v-else-if="isSuccess" :data="data" :is-fetching="isFetching"> </slot>
  <slot v-else-if="isError" name="error" :data="data" :error="error">
    <a-result
      status="500"
      title="Tivemos um problema inesperado"
      sub-title="Entre em contato com a equipe de suporte"
    >
      <template #extra>
        <a-button type="primary">Back Home</a-button>
      </template>
    </a-result>
  </slot>
</template>
